/**
 * Where a slide's text comes from, read off the rendered slide: the templates
 * mark the strategy's text (`data-v`, and every `.strategy-editable` field), so
 * anything else is the deck's own fixed copy. Creo is told which is which, so it
 * refuses to "edit" a label no one can change (prompts/agent/modules/strategy.ts).
 */
export type SlideTextSource = "label" | "value" | "mixed";

const VALUE = "[data-v], .strategy-editable";
/** Text that is the deck's chrome or a control, not a label anyone would ask about. */
const SKIP = `${VALUE}, button, figure, .st-page, .st-wanted, .strategy-template-opener-foot, [aria-hidden="true"]`;
const LINE = "h2, h3, h4, p, span, b, small, li, a, mark";
const MAX_LABELS = 15;

function textNodes(root: Node): Text[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) if (node.textContent?.trim()) nodes.push(node as Text);
  return nodes;
}

/** Whether a highlight on the slide covers the deck's fixed labels, the strategy's text, or both. */
export function selectionSource(range: Range, root: Element): SlideTextSource {
  let label = false;
  let value = false;
  for (const node of textNodes(root)) {
    if (!range.intersectsNode(node)) continue;
    if (node.parentElement?.closest(VALUE)) value = true;
    else label = true;
  }
  return value && label ? "mixed" : value ? "value" : "label";
}

/**
 * The slide's fixed labels ("What we learned", "Measured as"), each once, in
 * reading order. A line that mixes fixed words with the strategy's ("Four
 * channels, one job each.") is left out: its fixed words only frame a value.
 */
export function fixedLabels(root: Element): string[] {
  const seen = new Set<string>();
  for (const node of textNodes(root)) {
    const parent = node.parentElement;
    if (!parent || parent.closest(SKIP)) continue;
    const line = parent.closest(LINE) ?? parent;
    // Climb to the whole line: a highlighted phrase sits inside its title.
    const whole = line.parentElement?.closest("h2, h3, h4") ?? line;
    if (whole.querySelector(VALUE)) continue;
    const text = (whole.textContent ?? "").replace(/\s+/g, " ").trim();
    if (text.length < 3 || !/\p{L}/u.test(text)) continue;
    seen.add(text);
    if (seen.size >= MAX_LABELS) break;
  }
  return [...seen];
}
